import { Checkbox, Input, InputNumber, Radio, Select } from "antd"
import { DollarInputV2 } from "./DollarInputV2"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button as BootstrapButton, Col, Form, Row } from "react-bootstrap"
import { AddressComponent } from "../Common/AddressAutocomplete"
import * as st from "../State"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const FIELD_ORDER = ["presentAddress", "residenceOwnership", "monthlyRent"]

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const emptyAddress = {
    street: "",
    unit: "",
    city: "",
    county: "",
    state: "",
    zip: "",
    years: "",
    months: "",
}
const RESIDENCE_ADDRESS_PATTERN = "^[0-9]+\\s[\\p{L}\\p{N} !:,&()'?#+\\/@-]+$"

const emptyPreviousAddress = {
    ...emptyAddress,
    ownership: "",
    rent: "",
    rentperiod: "monthly",
}

const emptyResidence = {
    presentAddress: emptyAddress,
    mailingAddress: emptyAddress,
    previousAddresses: [],
    sameMailingAddress: true,
    residenceOwnership: "",
    monthlyRent: "",
}

const normalizeAddress = (address = {}) => ({
    ...emptyAddress,
    street: address.street || "",
    unit: address.unit || "",
    city: address.city || "",
    county: address.county || "",
    state: address.state || "",
    zip: address.zip || "",
    years: address.years || "",
    months: address.months || "",
})

const StructuredAddress = ({ id, label, address, onChange, placeholder, required = false }) => (
    <AddressComponent
        id={id}
        label={label}
        address={address?.street}
        unit={address?.unit}
        state={address?.state}
        county={address?.county}
        city={address?.city}
        zip={address?.zip}
        placeholder={placeholder}
        pattern={RESIDENCE_ADDRESS_PATTERN}
        required={required}
        onAddressLineChange={(value) => onChange("street", value)}
        onUnitChange={(value) => onChange("unit", value)}
        onStateChange={(value) => onChange("state", value)}
        onCountyChange={(value) => onChange("county", value)}
        onCityChange={(value) => onChange("city", value)}
        onZipChange={(value) => onChange("zip", value)}
    />
)

const toResidence = (participant) => {
    const presentAddress = normalizeAddress(participant?.presentAddress)
    // Legacy componentDidMount: empty present months default to "0".
    if (presentAddress.months === "") {
        presentAddress.months = "0"
    }
    return {
        ...emptyResidence,
        presentAddress,
        mailingAddress: normalizeAddress(participant?.mailingAddress),
        previousAddresses: Array.isArray(participant?.previousAddresses)
            ? participant.previousAddresses.map((address) => ({
                ...emptyPreviousAddress,
                ...normalizeAddress(address),
                ownership: address?.ownership || "",
                rent: address?.rent || "",
                rentperiod: address?.rentperiod || "monthly",
            }))
            : [],
        sameMailingAddress: participant?.sameMailingAddress !== false,
        residenceOwnership: participant?.residenceOwnership || "",
        monthlyRent: participant?.monthlyRent || "",
    }
}

const hasSamePresentAddress = (participant, primaryParticipant) => {
    const address = participant?.presentAddress || {}
    const primaryAddress = primaryParticipant?.presentAddress || {}
    if (!primaryAddress.street) {
        return false
    }
    // Legacy hasSameAddress compares street/state/county/city/zip only (not unit).
    return address.street === primaryAddress.street
        && address.state === primaryAddress.state
        && address.county === primaryAddress.county
        && address.city === primaryAddress.city
        && address.zip === primaryAddress.zip
}

const ResidenceV2 = ({ application, participant, primaryParticipant, saving, onSave, onBack }) => {
    const [residence, setResidence] = useState(emptyResidence)
    const [sameAsPrimary, setSameAsPrimary] = useState(false)
    const [fieldErrors, setFieldErrors] = useState({})
    const hydratingPartyId = useRef("")

    useEffect(() => {
        hydratingPartyId.current = participant?.partyId || ""
        setResidence(toResidence(participant))
        setSameAsPrimary(hasSamePresentAddress(participant, primaryParticipant))
        setFieldErrors({})
    }, [participant, primaryParticipant])

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const isCoborrower = participant && !participant.isPrimary
    const showMonths = useMemo(() => {
        const years = parseInt(residence.presentAddress.years || "0", 10)
        return residence.presentAddress.years !== "" && years < 2
    }, [residence.presentAddress.years])

    const needsPreviousAddress = useMemo(() => {
        if (!isCoborrower) {
            if (!residence.presentAddress.years) {
                return false
            }
            return parseInt(residence.presentAddress.years, 10) < 2
        }

        if (application?.cohabitingPartyId === participant?.partyId) {
            const primaryYears = primaryParticipant?.presentAddress?.years
            return Boolean(primaryYears) && parseInt(primaryYears, 10) < 2
        }

        if (!residence.presentAddress.years) {
            return false
        }
        return parseInt(residence.presentAddress.years, 10) < 2
    }, [application?.cohabitingPartyId, isCoborrower, participant?.partyId, primaryParticipant?.presentAddress?.years, residence.presentAddress.years])

    useEffect(() => {
        if (hydratingPartyId.current && hydratingPartyId.current === participant?.partyId) {
            hydratingPartyId.current = ""
            return
        }
        setResidence((prev) => {
            if (needsPreviousAddress && prev.previousAddresses.length === 0) {
                const presentAddress = {
                    ...prev.presentAddress,
                    months: prev.presentAddress.months === "" ? "0" : prev.presentAddress.months,
                }
                return {
                    ...prev,
                    presentAddress,
                    previousAddresses: [{ ...emptyPreviousAddress }],
                }
            }
            if (!needsPreviousAddress && prev.previousAddresses.length > 0) {
                return { ...prev, previousAddresses: [] }
            }
            return prev
        })
    }, [needsPreviousAddress, participant?.partyId])

    const setPresentAddress = (field, value) => {
        setResidence((prev) => {
            const nextValue = value == null ? "" : `${value}`
            const presentAddress = {
                ...prev.presentAddress,
                [field]: nextValue,
            }
            // Legacy updateYears: when years < 2, initialize months to "0".
            if (
                field === "years"
                && nextValue !== ""
                && parseInt(nextValue, 10) < 2
                && (presentAddress.months === "" || presentAddress.months == null)
            ) {
                presentAddress.months = "0"
            }
            return {
                ...prev,
                presentAddress,
            }
        })
        if (field === "street") {
            clearFieldError("presentAddress")
        }
    }

    const setMailingAddress = (field, value) => {
        setResidence((prev) => ({
            ...prev,
            mailingAddress: {
                ...prev.mailingAddress,
                [field]: value || "",
            },
        }))
    }

    const setPreviousAddress = (index, field, value) => {
        setResidence((prev) => ({
            ...prev,
            previousAddresses: prev.previousAddresses.map((address, addressIndex) => (
                addressIndex === index ? { ...address, [field]: value || "" } : address
            )),
        }))
    }

    const handleSameAsPrimary = (checked) => {
        setSameAsPrimary(checked)
        if (!checked || !primaryParticipant) {
            return
        }
        setResidence((prev) => ({
            ...prev,
            presentAddress: {
                ...prev.presentAddress,
                street: primaryParticipant.presentAddress?.street || "",
                unit: primaryParticipant.presentAddress?.unit || "",
                state: primaryParticipant.presentAddress?.state || "",
                county: primaryParticipant.presentAddress?.county || "",
                city: primaryParticipant.presentAddress?.city || "",
                zip: primaryParticipant.presentAddress?.zip || "",
            },
        }))
    }

    const setResidenceOwnership = (value) => {
        setResidence((prev) => ({ ...prev, residenceOwnership: value }))
        clearFieldError("residenceOwnership")
        if (value !== st.O_Rent) {
            clearFieldError("monthlyRent")
        }
    }

    const setMonthlyRent = (value) => {
        setResidence((prev) => ({ ...prev, monthlyRent: value == null ? "" : `${value}` }))
        clearFieldError("monthlyRent")
    }

    const hideCurrentAddressFields = isCoborrower && sameAsPrimary

    // Keep the same three rules as before — do not expand validation surface.
    const validateResidence = () => {
        const errors = {}

        // Legacy: only primary borrower street is required (coborrower street is not validated).
        if (!isCoborrower && !residence.presentAddress.street) {
            errors.presentAddress = "Please provide your current residential address."
        }
        if (!residence.residenceOwnership) {
            errors.residenceOwnership = "Please specify present address ownership!"
        }
        if (residence.residenceOwnership === st.O_Rent && !residence.monthlyRent) {
            errors.monthlyRent = "Please enter your monthly rent payment!"
        }

        const firstErrorKey = FIELD_ORDER.find((key) => !!errors[key]) || null
        return { errors, firstErrorKey }
    }

    const handleNext = () => {
        const { errors, firstErrorKey } = validateResidence()
        if (firstErrorKey) {
            setFieldErrors(errors)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setFieldErrors({})
        onSave(residence)
    }

    return (
        <>
            <div className="text-left mb-3 application-v2-form-body residence-v2">
                <div className="page-header page-header-row">
                    <span className="page-header-primary">Personal</span>
                    <span className="page-header-separator">/</span>
                    <span className="page-header-secondary">Current Residence</span>
                </div>

                {isCoborrower && (
                    <div className="residence-v2-section">
                        <Checkbox checked={sameAsPrimary} onChange={(event) => handleSameAsPrimary(event.target.checked)}>
                            <span style={{ fontSize: 14, fontWeight: 500, color: "#222", whiteSpace: "normal" }}>
                                Co-borrower has the same address as the primary borrower.
                            </span>
                        </Checkbox>
                    </div>
                )}

                {!hideCurrentAddressFields && (
                    <div className="residence-v2-section" data-field="presentAddress">
                        <StructuredAddress id="Address" label="*Current residential address" address={residence.presentAddress} onChange={setPresentAddress} placeholder="Street address" required />
                        <FieldError message={fieldErrors.presentAddress} />
                        <div className="application-v2-address-note">
                            <img src="/images/exclamation.svg" alt="" />No PO Box addresses are accepted
                        </div>
                    </div>
                )}

                <div className="residence-v2-section">
                    <Form.Label>How long at current address?</Form.Label>
                    <div className="residence-v2-years-row">
                        <div className="residence-v2-years-field">
                            <Input
                                suffix="Years"
                                size="large"
                                type="number"
                                min={0}
                                value={residence.presentAddress.years}
                                onChange={(event) => {
                                    const raw = event.target.value
                                    if (raw === "") {
                                        setPresentAddress("years", "")
                                        return
                                    }
                                    const n = parseInt(raw, 10)
                                    if (Number.isNaN(n)) {
                                        return
                                    }
                                    setPresentAddress("years", `${Math.max(0, n)}`)
                                }}
                            />
                        </div>
                        {showMonths && (
                            <div className="residence-v2-years-field">
                                <Input
                                    suffix="Months"
                                    size="large"
                                    type="number"
                                    value={residence.presentAddress.months}
                                    onChange={(event) => setPresentAddress("months", Math.min(parseInt(event.target.value || "0", 10), 11).toString())}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="residence-v2-section residence-v2-housing" data-field="residenceOwnership">
                    <Form.Label>*Housing</Form.Label>
                    <Radio.Group
                        name="radiogroup"
                        value={residence.residenceOwnership}
                        onChange={(event) => setResidenceOwnership(event.target.value)}
                        className="housing"
                    >
                        <Row className="radiogroupRow">
                            <Col md={4} xs={12}>
                                <Radio value={st.O_Rentfree}>No housing payment</Radio>
                            </Col>
                            <Col md={4} xs={12}>
                                <Radio value={st.O_Own}>Own</Radio>
                            </Col>
                            <Col md={4} xs={12}>
                                <Radio value={st.O_Rent}>Rent</Radio>
                            </Col>
                        </Row>
                    </Radio.Group>
                    <FieldError message={fieldErrors.residenceOwnership} />
                </div>

                {residence.residenceOwnership === st.O_Rent && (
                    <div className="residence-v2-section" data-field="monthlyRent">
                        <Form.Group className="inputSection">
                            <Form.Label>*How much is your monthly rent payment?</Form.Label>
                            <DollarInputV2
                                className="w-100"
                                style={{ maxWidth: 320 }}
                                value={residence.monthlyRent}
                                onChange={setMonthlyRent}
                                placeholder="Enter monthly rent"
                            />
                            <FieldError message={fieldErrors.monthlyRent} />
                        </Form.Group>
                    </div>
                )}

                {needsPreviousAddress && residence.previousAddresses.map((address, index) => (
                    <div className="residence-v2-section" key={`previous-address-${index}`}>
                        <StructuredAddress id={`previous-address-${index}`} label="Previous street address" address={address} onChange={(field, value) => setPreviousAddress(index, field, value)} placeholder="Street address" required />
                        <div className="application-v2-address-note">
                            <img src="/images/exclamation.svg" alt="" />No PO Box addresses are accepted
                        </div>
                        <div className="residence-v2-section">
                            <Row>
                                <Col xl={2} xs={12} className="mb-3 mb-xl-0 px-0 pr-xl-3">
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left">Years&nbsp;</Form.Label>
                                        <InputNumber
                                            value={address.years}
                                            min={0}
                                            size="large"
                                            className="w-100"
                                            onChange={(value) => setPreviousAddress(index, "years", value == null ? "" : `${value}`)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xl={2} xs={12} className="mb-3 mb-xl-0 px-0 pr-xl-3">
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left">Months&nbsp;</Form.Label>
                                        <Form.Control
                                            isValid={false}
                                            size="sm"
                                            required={parseInt(address.years || "0", 10) < 2}
                                            name="oldmonths"
                                            type="number"
                                            min={0}
                                            max={11}
                                            value={address.months}
                                            onChange={(event) => setPreviousAddress(index, "months", Math.min(parseInt(event.target.value || "0", 10), 11).toString())}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please provide months.
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col xl={3} xs={12} className="px-0">
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left">Ownership&nbsp;</Form.Label>
                                        <Select
                                            value={address.ownership}
                                            className="w-100"
                                            style={{ height: 44 }}
                                            onChange={(value) => setPreviousAddress(index, "ownership", value)}
                                        >
                                            <Select.Option value={st.O_Own}>Owned</Select.Option>
                                            <Select.Option value={st.O_Rent}>Rented</Select.Option>
                                            <Select.Option value={st.O_Rentfree}>Lived Rent Free</Select.Option>
                                        </Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                        {address.ownership === st.O_Rent && (
                            <div className="residence-v2-section">
                                <Form.Group className="inputSection">
                                    <Form.Label>Monthly rent</Form.Label>
                                    <DollarInputV2
                                        className="w-100"
                                        style={{ maxWidth: 320 }}
                                        value={address.rent}
                                        onChange={(value) => setPreviousAddress(index, "rent", value == null ? "" : `${value}`)}
                                        placeholder="Enter monthly rent"
                                    />
                                </Form.Group>
                            </div>
                        )}
                    </div>
                ))}

                <div className="residence-v2-section">
                    <Checkbox
                        className="applicant-checkbox"
                        onChange={(event) => setResidence((prev) => ({ ...prev, sameMailingAddress: event.target.checked }))}
                        checked={residence.sameMailingAddress}
                    >
                        <span className="wrap-text" style={{ fontSize: 18, fontWeight: 400, color: "#222222" }}>
                            Mailing Address is same as my current address
                        </span>
                    </Checkbox>

                    {!residence.sameMailingAddress && (
                        <div className="residence-v2-section">
                            <StructuredAddress id="mailingaddressline" label="*What's your mailing address" address={residence.mailingAddress} onChange={setMailingAddress} placeholder="Street address" required />
                            <div className="application-v2-address-note">
                                <img src="/images/exclamation.svg" alt="" />No PO Box addresses are accepted
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Row className="text-left application-bottom-bar residence-v2-bottom">
                <div className="d-flex align-items-center">
                    <BootstrapButton
                        id="previous"
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving}
                        className="operate-btn back mr-3"
                        onClick={onBack}
                    >
                        Back
                    </BootstrapButton>
                    <BootstrapButton
                        id="next"
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving}
                        className="text-center operate-btn btn"
                        onClick={handleNext}
                    >
                        Next
                    </BootstrapButton>
                </div>
            </Row>
        </>
    )
}

export default ResidenceV2
